import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { ApiService } from '../services/apiService'
import { TaskManager } from '../services/taskManager'
import { clearDailyTaskExecutionData, getLocalDayKey } from '../utils/dailyRuntime.js'
import { API_TRANSPORT, getProxySettings, subscribeProxySettings } from '../services/proxySettings.js'
import { ProxyRecovery } from '../services/proxyRecovery.js'
import { prepareScheduledRecovery } from '../services/scheduledRecovery.js'

export const useAppStore = create(
  persist(
    (set, get) => ({
      // 应用配置
      config: {
        referrer: "http://111.231.174.15:9100/",
        hjsgServer: "s1",
        activitySkipEnabled: true,
        dailyRuntimeResetEnabled: true
      },

      // 最近一次完成跨日重置的本地日期（YYYY-MM-DD）
      lastDailyRuntimeResetDate: null,

      // 全局服务实例
      apiService: null,

      // 账号管理
      accounts: {},
      activeAccount: null,
      loginLocks: {},

      // 任务管理
      taskManager: null,

      // 全局任务配置
      globalTaskConfigs: {},

      // 任务执行数据存储（按任务ID和账号存储）
      taskExecutionData: {},

      // 任务组管理
      taskGroups: {},

      // 用户组管理
      userGroups: {},

      // 定时任务管理
      scheduledJobs: {},

      // UI状态
      sidebarOpen: false,
      currentPage: 'account-management',

      // 任务选择器筛选状态（持久化）
      taskSelector: {
        selectedCategory: 'all'
      },

      // 已选择任务ID集合（持久化）
      selectedTaskIds: [],
      selectedTaskIdsByRuntime: {
        browser: [],
        backend: []
      },

      // 运行模式（纯浏览器版固定为 browser）
      executionRuntime: 'browser',

      // 初始化应用
      initializeApp: async () => {
        // 创建全局ApiService实例
        const apiService = new ApiService(get().config)

        const taskManager = new TaskManager()
        await taskManager.initialize()

        // 为taskManager设置appStore引用（使用代理以获取最新state）
        const appStoreProxy = {
          get accounts() { return get().accounts },
          get apiService() { return get().apiService },
          get config() { return get().config },
          updateConfig: (patch) => get().updateConfig(patch),
          updateUserInfo: (accountKey, userInfo) => get().updateUserInfo(accountKey, userInfo),
          updateAccountAuth: (accountKey, payload) => get().updateAccountAuth(accountKey, payload),
          setLoginLock: (accountKey, lockToken) => get().setLoginLock(accountKey, lockToken),
          getGlobalTaskConfig: (taskId) => get().getGlobalTaskConfig(taskId),
          updateGlobalTaskConfig: (taskId, config) => get().updateGlobalTaskConfig(taskId, config),
          setTaskData: (taskId, accountKey, data) => get().setTaskData(taskId, accountKey, data),
          getTaskData: (taskId, accountKey) => get().getTaskData(taskId, accountKey),
          clearTaskData: (taskId, accountKey) => get().clearTaskData(taskId, accountKey),
        }
        taskManager.setAppStore(appStoreProxy)
        const syncTransportPause = () => {
          if (API_TRANSPORT !== 'proxy') return
          if (getProxySettings().paused) taskManager.pauseForTransport()
        }

        set({
          taskManager,
          apiService,
          _unsubscribeProxySettings: (() => {
            get()._unsubscribeProxySettings?.()
            return subscribeProxySettings(syncTransportPause)
          })(),
        })
        syncTransportPause()
        get().proxyRecovery?.dispose()
        if (API_TRANSPORT === 'proxy') set({ proxyRecovery: new ProxyRecovery({
          getRuntime: () => ({ ...get(), addLog: taskManager.logFunction }),
        }) })

        // 清理过期的定时任务
        get().cleanupExpiredJobs()
        taskManager.restorePersistentTaskPlans()

        console.log("应用初始化完成")
      },

      setExecutionRuntime: () => {
        // 纯浏览器版固定为 browser 运行模式
        set(state => {
          const selectedByRuntime = state.selectedTaskIdsByRuntime || {}
          return {
            executionRuntime: 'browser',
            selectedTaskIds: selectedByRuntime.browser || state.selectedTaskIds || []
          }
        })
      },

      // 获取当前运行数据（浏览器版）
      getRuntimeData: () => {
        const state = get()
        return {
          accounts: state.accounts,
          activeAccount: state.activeAccount,
          globalTaskConfigs: state.globalTaskConfigs,
          taskExecutionData: state.taskExecutionData,
          taskGroups: state.taskGroups,
          userGroups: state.userGroups,
          scheduledJobs: state.scheduledJobs
        }
      },

      saveRuntimeData: async (nextData) => {
        set({
          accounts: nextData.accounts || {},
          activeAccount: nextData.activeAccount || null,
          globalTaskConfigs: nextData.globalTaskConfigs || {},
          taskExecutionData: nextData.taskExecutionData || {},
          taskGroups: nextData.taskGroups || {},
          userGroups: nextData.userGroups || {},
          scheduledJobs: nextData.scheduledJobs || {}
        })
        return get().getRuntimeData()
      },

      updateRuntimeData: async (updater) => {
        const currentData = get().getRuntimeData()
        const nextData = typeof updater === 'function' ? updater(currentData) : updater
        return get().saveRuntimeData({
          ...currentData,
          ...nextData
        })
      },

      getRuntimeSelectedTaskIds: () => {
        const state = get()
        const selectedByRuntime = state.selectedTaskIdsByRuntime || {}
        if (Array.isArray(selectedByRuntime.browser)) {
          return selectedByRuntime.browser
        }
        return state.selectedTaskIds || []
      },

      setRuntimeSelectedTaskIds: (ids) => {
        const nextIds = Array.isArray(ids) ? ids : []
        set(current => ({
          selectedTaskIds: nextIds,
          selectedTaskIdsByRuntime: {
            ...(current.selectedTaskIdsByRuntime || {}),
            browser: nextIds
          }
        }))
      },

      // 账号管理方法
      addAccount: (username, server, password, token, userInfo) => {
        if (get().taskManager?.migrationPaused) return
        const { accounts } = get()
        const serverAddress = String(server || "").trim()
        const accountKey = `${username}_S${serverAddress}`
        set({
          accounts: {
            ...accounts,
            [accountKey]: {
              username,
              server: serverAddress,
              password,
              token,
              userInfo,
              tokenUid: userInfo?.uid,
            }
          },
          activeAccount: accountKey
        })
        console.log(`账号 ${username} (${serverAddress}) 添加成功`)
      },

      removeAccount: (accountKey) => {
        const { accounts, activeAccount } = get()
        const account = accounts[accountKey]
        const newAccounts = { ...accounts }
        delete newAccounts[accountKey]

        set({
          accounts: newAccounts,
          activeAccount: activeAccount === accountKey ? null : activeAccount
        })

        if (account) {
          console.log(`账号 ${account.username} (${account.server}) 已删除`)
        } else {
          console.log(`账号 ${accountKey} 已删除`)
        }
      },

      setActiveAccount: (accountKey) => {
        const { accounts } = get()
        const account = accounts[accountKey]
        set({ activeAccount: accountKey })

        if (account) {
          console.log(`已切换到账号: ${account.username} (${account.server})`)
        } else {
          console.log(`已切换到账号: ${accountKey}`)
        }
      },

      updateUserInfo: (accountKey, userInfo) => {
        if (get().taskManager?.migrationPaused) return
        const { accounts } = get()
        if (accounts[accountKey]) {
          set({
            accounts: {
              ...accounts,
              [accountKey]: {
                ...accounts[accountKey],
                userInfo
              }
            }
          })
        }
      },

      // 更新账号认证信息（token、tokenUid）
      updateAccountAuth: (accountKey, { token, tokenUid }) => {
        if (get().taskManager?.migrationPaused) return
        const { accounts } = get()
        if (!accounts[accountKey]) return
        set({
          accounts: {
            ...accounts,
            [accountKey]: {
              ...accounts[accountKey],
              token: token !== undefined ? token : accounts[accountKey].token,
              tokenUid: tokenUid !== undefined ? tokenUid : accounts[accountKey].tokenUid
            }
          }
        })
      },

      // 登录锁（用于观察同一账号的重新登录是否在进行中；互斥逻辑在 TaskBase.withLoginLock）
      setLoginLock: (accountKey, lockToken) => {
        const { loginLocks } = get()
        const next = { ...loginLocks }
        if (lockToken === null || lockToken === undefined) {
          delete next[accountKey]
        } else {
          next[accountKey] = lockToken
        }
        set({ loginLocks: next })
      },

      // 校验账号对象与 accountKey 的一致性，防止持久化/多标签页竞争导致的串号
      // 仅做安全修复：目标 key 已存在且不是同一账号时不迁移（避免丢数据）
      validateAccountIntegrity: () => {
        const { accounts } = get()
        const mismatches = []
        Object.entries(accounts).forEach(([accountKey, account]) => {
          if (!account || !account.username) return
          const expectedKey = `${account.username}_S${account.server || ""}`
          if (expectedKey !== accountKey) {
            mismatches.push({ accountKey, expectedKey, username: account.username })
          }
        })

        if (mismatches.length > 0) {
          console.warn(`检测到账号串号（accountKey 与 username 不一致）：`, JSON.stringify(mismatches))
        }

        const newAccounts = { ...accounts }
        let fixed = 0
        mismatches.forEach(({ accountKey, expectedKey }) => {
          const targetExists = newAccounts[expectedKey] !== undefined
          const targetIsSame = targetExists && newAccounts[expectedKey] === newAccounts[accountKey]
          if (!targetExists || targetIsSame) {
            newAccounts[expectedKey] = newAccounts[accountKey]
            delete newAccounts[accountKey]
            fixed++
          }
        })

        if (fixed > 0) {
          console.warn(`已安全修复 ${fixed} 个账号串号`)
          set({ accounts: newAccounts })
        }

        // 检查定时任务的账号引用是否存在（防止修复串号后产生悬空引用）
        const validKeys = new Set(Object.keys(get().accounts))
        const scheduledJobs = get().scheduledJobs || {}
        Object.values(scheduledJobs).forEach(job => {
          if (job && Array.isArray(job.accounts)) {
            job.accounts.forEach(accKey => {
              if (!validKeys.has(accKey)) {
                console.warn(`定时任务 ${job.id || "?"} 引用了不存在的账号: ${accKey}，请检查该定时任务的账号列表`)
              }
            })
          }
        })

        return { fixed, mismatches }
      },

      // 登录方法
      login: async (server, username, password) => {
        try {
          console.log(`正在登录账号 ${username} (${server})...`)

          const { apiService } = get()
          if (!apiService) {
            throw new Error("ApiService未初始化")
          }

          const result = await apiService.login(server, username, password)

          if (result.success) {
            get().addAccount(username, server, password, result.token, result.userInfo)
            return { success: true }
          } else {
            console.log(`登录失败: ${result.error}`)
            return { success: false, error: result.error }
          }
        } catch (error) {
          console.log(`登录异常: ${error.message}`)
          return { success: false, error: error.message }
        }
      },

      // 刷新用户信息
      refreshUserInfo: async (accountKey) => {
        const { accounts, apiService } = get()
        const account = accounts[accountKey]
        if (!account || !apiService) return

        try {
          const userInfo = await apiService.getUserInfo(account.server, account.token)

          if (userInfo) {
            get().updateUserInfo(accountKey, userInfo)
            console.log(`账号 ${account.username} (${account.server}) 信息已刷新`)
          }
        } catch (error) {
          console.log(`刷新用户信息失败: ${error.message}`)
        }
      },

      // UI状态管理
      toggleSidebar: () => {
        set(state => ({ sidebarOpen: !state.sidebarOpen }))
      },

      closeSidebar: () => {
        set({ sidebarOpen: false })
      },

      setCurrentPage: (page) => {
        set({ currentPage: page })
      },

      // 更新任务选择器筛选状态
      setTaskSelectorState: (partial) => {
        set(state => ({
          taskSelector: {
            ...state.taskSelector,
            ...partial
          }
        }))
      },

      // 更新已选择任务ID集合
      setSelectedTaskIds: (ids) => {
        get().setRuntimeSelectedTaskIds(ids)
      },

      // 更新应用配置
      updateConfig: (patch) => {
        set(state => ({
          config: {
            ...state.config,
            ...patch
          }
        }))
        console.log('应用配置已更新:', patch)
      },

      // 全局任务配置管理
      updateGlobalTaskConfig: (taskId, config) => {
        if (get().taskManager?.migrationPaused) return

        set(state => ({
          globalTaskConfigs: {
            ...state.globalTaskConfigs,
            [taskId]: config
          }
        }))
      },

      getGlobalTaskConfig: (taskId) => {
        return get().globalTaskConfigs?.[taskId] || {}
      },

      // 任务执行数据管理
      setTaskData: (taskId, accountKey, data) => {
        if (get().taskManager?.migrationPaused) return
        set(state => ({
          taskExecutionData: {
            ...state.taskExecutionData,
            [`${taskId}_${accountKey}`]: data
          }
        }))
      },

      getTaskData: (taskId, accountKey) => {
        return get().taskExecutionData?.[`${taskId}_${accountKey}`] || null
      },

      clearTaskData: (taskId, accountKey) => {
        const { taskExecutionData } = get()
        const key = `${taskId}_${accountKey}`
        const newTaskExecutionData = { ...taskExecutionData }
        delete newTaskExecutionData[key]

        set({ taskExecutionData: newTaskExecutionData })
      },

      resetDailyTaskExecutionData: () => {
        const { taskExecutionData } = get()
        set({ taskExecutionData: clearDailyTaskExecutionData(taskExecutionData) })
      },

      markDailyRuntimeReset: (dateKey) => {
        set({ lastDailyRuntimeResetDate: dateKey })
      },

      resetScheduledJobsForDailyRuntime: () => {
        set(state => ({
          scheduledJobs: Object.fromEntries(
            Object.entries(state.scheduledJobs || {}).map(([jobId, job]) => [
              jobId,
              job?.status === 'executing' ? { ...job, status: 'scheduled' } : job
            ])
          )
        }))
      },

      // 任务组管理方法
      addTaskGroup: (groupData) => {
        const groupId = `task_group_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        set(state => ({
          taskGroups: {
            ...state.taskGroups,
            [groupId]: groupData
          }
        }))
        console.log(`任务组 "${groupData.name}" 创建成功`)
        return groupId
      },

      updateTaskGroup: (groupId, groupData) => {
        set(state => ({
          taskGroups: {
            ...state.taskGroups,
            [groupId]: groupData
          }
        }))
        console.log(`任务组 "${groupData.name}" 更新成功`)
      },

      deleteTaskGroup: (groupId) => {
        const { taskGroups } = get()
        const group = taskGroups[groupId]
        const newTaskGroups = { ...taskGroups }
        delete newTaskGroups[groupId]

        set({ taskGroups: newTaskGroups })

        if (group) {
          console.log(`任务组 "${group.name}" 已删除`)
        }
      },

      getTaskGroup: (groupId) => {
        const { taskGroups } = get()
        return taskGroups[groupId]
      },

      // 用户组管理方法
      addUserGroup: (groupData) => {
        const groupId = `user_group_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        set(state => ({
          userGroups: {
            ...state.userGroups,
            [groupId]: groupData
          }
        }))
        console.log(`用户组 "${groupData.name}" 创建成功`)
        return groupId
      },

      updateUserGroup: (groupId, groupData) => {
        set(state => ({
          userGroups: {
            ...state.userGroups,
            [groupId]: groupData
          }
        }))
        console.log(`用户组 "${groupData.name}" 更新成功`)
      },

      deleteUserGroup: (groupId) => {
        const { userGroups } = get()
        const group = userGroups[groupId]
        const newUserGroups = { ...userGroups }
        delete newUserGroups[groupId]

        set({ userGroups: newUserGroups })

        if (group) {
          console.log(`用户组 "${group.name}" 已删除`)
        }
      },

      getUserGroup: (groupId) => {
        const { userGroups } = get()
        return userGroups[groupId]
      },

      // 定时任务管理方法
      prepareScheduledJobsForResume: ({ catchUp = true } = {}) => {
        const state = get()
        const today = getLocalDayKey()
        const resetDailyPlans = state.config?.dailyRuntimeResetEnabled !== false && state.lastDailyRuntimeResetDate !== today
        // 跨日清理必须在放行新任务之前完成，避免恢复后的当日补跑被午夜重置取消。
        if (resetDailyPlans) {
          state.taskManager.resetDailyRuntime()
          state.resetDailyTaskExecutionData()
          state.resetScheduledJobsForDailyRuntime()
          state.markDailyRuntimeReset(today)
        }
        const { scheduledJobs, dueJobs } = prepareScheduledRecovery(get().scheduledJobs, new Date(), { catchUp })
        set({ scheduledJobs })
        return { dueJobs, resetDailyPlans }
      },

      addScheduledJob: (jobData) => {
        const jobId = `scheduled_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        const job = {
          ...jobData,
          id: jobId,
          createdAt: new Date().toISOString(),
          status: 'scheduled'
        }

        set(state => ({
          scheduledJobs: {
            ...state.scheduledJobs,
            [jobId]: job
          }
        }))

        console.log(`定时任务 "${jobId}" 已保存到本地存储`)
        return jobId
      },

      updateScheduledJob: (jobId, updates) => {
        set(state => ({
          scheduledJobs: {
            ...state.scheduledJobs,
            [jobId]: {
              ...state.scheduledJobs[jobId],
              ...updates
            }
          }
        }))
      },

      removeScheduledJob: (jobId) => {
        const { scheduledJobs } = get()
        const job = scheduledJobs[jobId]
        const newScheduledJobs = { ...scheduledJobs }
        delete newScheduledJobs[jobId]

        set({ scheduledJobs: newScheduledJobs })

        if (job) {
          console.log(`定时任务 "${jobId}" 已从本地存储中删除`)
        }
      },

      getScheduledJob: (jobId) => {
        const { scheduledJobs } = get()
        return scheduledJobs[jobId]
      },

      getAllScheduledJobs: () => {
        const { scheduledJobs } = get()
        return Object.values(scheduledJobs)
      },

      // 清理过期的定时任务（可选，用于维护）
      cleanupExpiredJobs: () => {
        const { scheduledJobs } = get()
        const now = new Date()
        const validJobs = {}

        Object.entries(scheduledJobs).forEach(([jobId, job]) => {
          // 保留状态为 scheduled 或最近24小时内的任务
          const jobTime = new Date(job.scheduledTime)
          const timeDiff = now.getTime() - jobTime.getTime()
          const isRecent = timeDiff < 24 * 60 * 60 * 1000 // 24小时

          if (job.status === 'scheduled' || isRecent) {
            validJobs[jobId] = job
          }
        })

        set({ scheduledJobs: validJobs })
        console.log('已清理过期的定时任务')
      },

      // 格式化数字
      formatNumber: (num) => {
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
      }
    }),
    {
      name: 'hanjiang-helper-storage',
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => (state) => {
        if (state && typeof state.validateAccountIntegrity === "function") {
          try {
            state.validateAccountIntegrity()
          } catch (error) {
            console.warn("账号一致性校验失败:", error)
          }
        }
      },
      partialize: (state) => ({
        accounts: state.accounts,
        activeAccount: state.activeAccount,
        config: state.config,
        lastDailyRuntimeResetDate: state.lastDailyRuntimeResetDate,
        globalTaskConfigs: state.globalTaskConfigs, // 持久化全局任务配置
        taskExecutionData: state.taskExecutionData, // 持久化任务执行数据
        taskGroups: state.taskGroups, // 持久化任务组
        userGroups: state.userGroups, // 持久化用户组
        scheduledJobs: state.scheduledJobs, // 持久化定时任务
        taskSelector: state.taskSelector,
        selectedTaskIds: state.selectedTaskIds,
        selectedTaskIdsByRuntime: state.selectedTaskIdsByRuntime
      })
    }
  )
)
